import { usePageTitle } from '../hooks/usePageTitle';
import { useState } from 'react';

const CardGamePage = () => {
    usePageTitle('Card Game | Quinexa');

    // --- CREDITS INITIALIZATION ---
    const getInitialCredits = () => {
        const storedCredits = localStorage.getItem('QuinexaCredits');
        if (storedCredits === null) {
            localStorage.setItem('QuinexaCredits', '1000');
            return 1000;
        }
        return parseInt(storedCredits, 10);
    };

    // --- STATES ---
    const [credits, setCredits] = useState(getInitialCredits());
    const [bet, setBet] = useState(50);
    const [isPlaying, setIsPlaying] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState(JSON.parse(localStorage.getItem('gameHistory')) || []);

    const [playerCard, setPlayerCard] = useState({ value: '?', suit: '', isRed: false, hidden: true });
    const [dealerCard, setDealerCard] = useState({ value: '?', suit: '', isRed: false, hidden: true });

    const suits = ['H', 'D', 'C', 'S'];
    const values = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
    const suitSymbols = { H: '♥', D: '♦', C: '♣', S: '♠' };

    // --- LOGIC ---
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const drawCard = () => {
        const suit = suits[Math.floor(Math.random() * suits.length)];
        const value = values[Math.floor(Math.random() * values.length)];
        return {
            value,
            symbol: suitSymbols[suit],
            isRed: suit === 'H' || suit === 'D',
            hidden: false
        };
    };

    const getCardValue = (val) => {
        if (val === 'A') return 14;
        if (val === 'K') return 13;
        if (val === 'Q') return 12;
        if (val === 'J') return 11;
        return parseInt(val, 10);
    };

    const handlePlay = async () => {
        if (bet < 10) return alert('Minimum bet is 10 credits');
        if (credits < bet) return alert('Not enough credits');

        setIsPlaying(true);
        setResult(null);
        setPlayerCard(prev => ({ ...prev, hidden: true }));
        setDealerCard(prev => ({ ...prev, hidden: true }));

        const currentCredits = credits - bet;
        updateGlobalCredits(currentCredits);

        await sleep(600);
        const pCard = drawCard();
        setPlayerCard(pCard);

        await sleep(800);
        const dCard = drawCard();
        setDealerCard(dCard);

        const pVal = getCardValue(pCard.value);
        const dVal = getCardValue(dCard.value);

        let won = false;
        let tie = false;
        let finalCredits = currentCredits;

        if (pVal > dVal) {
            won = true;
            finalCredits += bet * 2;
        } else if (pVal === dVal) {
            tie = true;
            finalCredits += bet;
        }

        setResult({ won, tie, amount: won ? bet * 2 : (tie ? 0 : bet) });
        updateGlobalCredits(finalCredits);
        addHistory('Cards', bet, `${pCard.value} vs ${dCard.value}`, won || tie);

        setIsPlaying(false);
    };

    const updateGlobalCredits = (newAmount) => {
        setCredits(newAmount);
        localStorage.setItem('QuinexaCredits', newAmount.toString());

        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        if (currentUser) {
            currentUser.credits = newAmount;
            localStorage.setItem('currentUser', JSON.stringify(currentUser));
        }
    };

    const addHistory = (game, bet, res, won) => {
        const newEntry = { id: Date.now(), game, bet, result: res, won, date: new Date().toLocaleTimeString() };
        const updatedHistory = [newEntry, ...history].slice(0, 5);
        setHistory(updatedHistory);
        localStorage.setItem('gameHistory', JSON.stringify(updatedHistory));
    };

    return (
        <main className="main">
            <div className="container">
                <section className="game-page">
                    <div className="game-container">
                        <div className="game-main">
                            {/* Header kısmı aynı kalıyor... */}
                            <div className="game-header">
                                <div className="game-header-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                        <rect x="3" y="3" width="14" height="18" rx="2" />
                                        <path d="M10 8L7 12l3 4" />
                                    </svg>
                                </div>
                                <h1 className="game-header-title">Card Game</h1>
                            </div>

                            <div className="game-area">
                                <div className="cards-container">
                                    <div className="card-deck">
                                        <h4>Your Card</h4>
                                        <div className={`playing-card ${playerCard.hidden ? 'hidden' : ''} ${playerCard.isRed ? 'red' : ''}`}>
                                            {playerCard.value}{playerCard.symbol}
                                        </div>
                                    </div>

                                    <div className="card-deck">
                                        <h4>Dealer's Card</h4>
                                        <div className={`playing-card ${dealerCard.hidden ? 'hidden' : ''} ${dealerCard.isRed ? 'red' : ''}`}>
                                            {dealerCard.value}{dealerCard.symbol}
                                        </div>
                                    </div>
                                </div>

                                <div className="game-controls">
                                    <div className="bet-input">
                                        <label>Bet:</label>
                                        <input
                                            type="number"
                                            value={bet}
                                            onChange={(e) => setBet(parseInt(e.target.value) || 0)}
                                            disabled={isPlaying}
                                        />
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handlePlay}
                                        disabled={isPlaying}
                                    >
                                        {isPlaying ? 'Dealing...' : 'Deal Cards'}
                                    </button>
                                </div>

                                <div className="game-result">
                                    {result && (
                                        <>
                                            <div className="result-label">
                                                {result.tie ? 'TIE - BET RETURNED' : (result.won ? 'YOU WON!' : 'DEALER WINS')}
                                            </div>
                                            <div className={`result-value ${result.won ? 'win' : (result.tie ? '' : 'lose')}`}>
                                                {result.won ? `+${result.amount}` : (result.tie ? '0' : `-${result.amount}`)}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="game-sidebar">
                            <div className="sidebar-card">
                                <h3>Your Credits</h3>
                                {/* Yeni global state'i burada gösteriyoruz */}
                                <div className="credits-display">{credits.toLocaleString()}</div>
                            </div>

                            <div className="sidebar-card">
                                <h3>Recent Plays</h3>
                                <div className="history-list">
                                    {history.length === 0 ? (
                                        <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No plays yet</p>
                                    ) : (
                                        history.map(item => (
                                            <div key={item.id} className="history-item">
                                                <span>{item.result}</span> |
                                                <span className={item.won ? 'text-win' : 'text-lose'}>
                                                    {item.won ? ' Win' : ' Loss'}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default CardGamePage;